import { ComponentType, ElementType, ReactNode } from 'react';
import { Position, Size } from '@optimus-web/common';
import { IconName } from '@optimus-web/icons';
import { AvatarProps } from '../Avatar';
import { Badge } from '../Badge';
import { Icon, IconProps } from '../Icon';
import { Tooltip } from '../Tooltip';
import {
    MenuListButton,
    MenuListContent,
    MenuListExpandIcon,
    MenuListItemElement,
    MenuListSubtitle,
    MenuListText,
    MenuListTextTitle,
} from './style';

interface ElementProps {
    onClick?: () => void;
}

export enum LeftContentAllowedComponents {
    AvatarComponent = 'avatar',
    IconComponent = 'icon',
}

interface LeftContentProps {
    component: LeftContentAllowedComponents;
    props: Partial<IconProps | AvatarProps>;
}
export interface MenuListItemProps<T = ElementProps> {
    children?: JSX.Element | null;
    id: string;
    content?: ReactNode;
    leftContent?: LeftContentProps;
    element?: ElementType | ComponentType<T>;
    elementProps?: ElementProps | T;
    items?: MenuListItemProps<T>[];
    badgeContent?: string;
    expanded?: boolean;
    expandable?: boolean;
    onItemClick?: (id: string, expanded?: boolean) => void;
    active?: boolean;
    collapsed?: boolean;
    size?: Omit<Size, 'small'>;
    subtitle?: ReactNode;
    avatar?: (props: AvatarProps) => void;
}

const getLeftContentComponent = (
    leftContent?: LeftContentProps,
    collapsed?: boolean,
    content?: ReactNode,
    badgeContent?: string,
    avatar?: (props: AvatarProps) => void
) => {
    if (!leftContent || !Object.keys(leftContent).length) {
        return null;
    }

    const { component, props } = leftContent;
    const TooltipedIcon = Tooltip(Icon);

    if (component === LeftContentAllowedComponents.IconComponent && !collapsed) {
        return <Icon {...(props as IconProps)} />;
    } else if (component === LeftContentAllowedComponents.IconComponent && collapsed && !badgeContent) {
        return <TooltipedIcon {...(props as IconProps)} tooltip={content} tooltipPosition={Position.Right} />;
    } else if (component === LeftContentAllowedComponents.IconComponent && collapsed && badgeContent) {
        return (
            <Badge variant="primary" amount={badgeContent}>
                <TooltipedIcon {...(props as IconProps)} tooltip={content} />
            </Badge>
        );
    } else if (avatar && component === LeftContentAllowedComponents.AvatarComponent) {
        return avatar(props as AvatarProps);
    }
};

export const MenuListItem = <T,>({
    children,
    element = 'button',
    elementProps,
    leftContent,
    content,
    onItemClick,
    badgeContent,
    expandable,
    expanded = false,
    collapsed = false,
    active = false,
    size = 'medium',
    subtitle,
    avatar,
}: Omit<MenuListItemProps<T>, 'id'>) => {
    const leftContentComponent = getLeftContentComponent(leftContent, collapsed, content, badgeContent, avatar);

    return (
        <MenuListItemElement collapsed={collapsed}>
            <MenuListButton
                onClick={onItemClick}
                as={element}
                active={active}
                collapsed={collapsed}
                size={size}
                {...(expandable &&
                    !collapsed && {
                        'aria-label': expanded ? 'collapse' : 'expand',
                        'aria-expanded': expanded,
                    })}
                {...elementProps}
            >
                <MenuListContent as="span" itemAlignment="center">
                    {leftContentComponent ? <>{leftContentComponent}</> : null}
                    {collapsed ? null : (
                        <MenuListText>
                            <MenuListTextTitle>{content}</MenuListTextTitle>
                            {subtitle ? <MenuListSubtitle>{subtitle}</MenuListSubtitle> : null}
                        </MenuListText>
                    )}
                    {badgeContent && !collapsed ? <Badge variant="primary">{badgeContent}</Badge> : null}
                </MenuListContent>
                {expandable && !collapsed ? (
                    <MenuListExpandIcon
                        value={IconName.ChevronDown}
                        size="small"
                        aria-label={expanded ? 'collapse' : 'expand'}
                        expanded={expanded}
                        aria-expanded={expanded}
                    />
                ) : null}
            </MenuListButton>
            {children}
        </MenuListItemElement>
    );
};
